<template>
  <div class="page-editor">
    <!-- 顶部工具栏 -->
    <div class="toolbar">
      <div class="tool-title">{{ title }}</div>
      <div class="tool-items">
        <div
          class="tool-item"
          :class="{ active: activeTool === 'bigImage' }"
          @click="handleAddComponent('bigImage')"
        >
          <div class="tool-icon big-image-icon"></div>
          <span class="tool-text">大图</span>
        </div>
        <div
          class="tool-item"
          :class="{ active: activeTool === 'smallImage' }"
          @click="handleAddMultipleComponents('smallImage')"
        >
          <div class="tool-icon small-image-icon"></div>
          <span class="tool-text">小图</span>
        </div>
        <div
          class="tool-item"
          :class="{ active: activeTool === 'video' }"
          @click="handleAddMultipleComponents('video')"
        >
          <div class="tool-icon video-icon"></div>
          <span class="tool-text">视频</span>
        </div>
        <div
          class="tool-item"
          :class="{ active: activeTool === 'text' }"
          @click="handleAddTextComponent"
        >
          <div class="tool-icon text-icon"></div>
          <span class="tool-text">文字</span>
        </div>
        <div
          class="tool-item"
          :class="{ active: activeTool === 'tag' }"
          @click="handleAddMultipleComponents('tag')"
        >
          <div class="tool-icon tag-icon"></div>
          <span class="tool-text">标签</span>
        </div>
      </div>
    </div>

    <!-- 内容展示区 -->
    <div class="content-area" ref="contentArea">
      <div
        v-for="(item, index) in componentList"
        :key="item.id"
        class="component-item"
        :class="{
          active: activeIndex === index,
          'horizontal-layout': shouldUseHorizontalLayout(item.type)
        }"
        @click="handleSetActive(index)"
      >
        <div class="component-type-tag">
          <span>{{ getTypeText(item.type) }}</span>
        </div>

        <div v-if="item.type === 'bigImage'" class="big-image-component">
          <img
            :src="item.content"
            class="big-image"
            @click="handleEditComponent(index)"
          />
        </div>

        <div v-if="item.type === 'smallImage'" class="small-image-component">
          <img
            :src="item.content"
            class="small-image"
            @click="handleEditComponent(index)"
          />
        </div>

        <div v-if="item.type === 'video'" class="video-component">
          <video
            :src="item.content"
            controls
            class="video-player"
            @click="handleEditComponent(index)"
          ></video>
        </div>

        <div v-if="item.type === 'text'" class="text-component">
          <span class="text-content" @click="handleEditTextDirectly(index)">{{
            item.content || '点击输入文字...'
          }}</span>
        </div>

        <div v-if="item.type === 'tag'" class="tag-component">
          <div class="tag-content" @click="handleEditComponent(index)">
            <span class="tag-text">{{ item.content }}</span>
          </div>
        </div>

        <div class="component-actions">
          <div class="action-btn-group">
            <el-button
              size="mini"
              type="primary"
              plain
              @click.stop="handleMoveUp(index)"
            >
              上移
            </el-button>
            <el-button
              size="mini"
              type="primary"
              plain
              @click.stop="handleMoveDown(index)"
            >
              下移
            </el-button>
            <el-button
              size="mini"
              type="danger"
              plain
              @click.stop="handleDeleteComponent(index)"
            >
              删除
            </el-button>
          </div>
        </div>
      </div>

      <div v-if="componentList.length === 0" class="empty-state">
        <div class="empty-icon"></div>
        <div class="empty-title">页面内容为空</div>
        <div class="empty-desc">请从上方工具栏选择组件添加到页面</div>
      </div>
    </div>

    <div class="footer" v-if="showFooter">
      <div class="footer-content">
        <div class="component-count">已添加 {{ componentList.length }} 个组件</div>
        <div class="footer-actions"></div>
      </div>
    </div>

    <!-- 编辑弹窗 -->
    <el-dialog
      :title="'编辑' + getTypeText(editingComponent.type)"
      :visible.sync="showEditModal"
      width="560px"
      append-to-body
      @close="closeModal"
    >
      <template v-if="editingComponent.type === 'bigImage' || editingComponent.type === 'smallImage'">
        <el-form label-width="80px">
          <el-form-item label="图片URL:">
            <el-input
              v-model="editingComponent.content"
              placeholder="请输入图片URL"
              style="width: 100%"
            >
              <el-button slot="append" @click="handleChooseImage" :loading="uploading">选择图片</el-button>
            </el-input>
          </el-form-item>
        </el-form>
        <div v-if="editingComponent.content" class="image-preview">
          <img :src="editingComponent.content" class="preview-image" />
        </div>
      </template>

      <template v-if="editingComponent.type === 'video'">
        <el-form label-width="80px">
          <el-form-item label="视频URL:">
            <el-input
              v-model="editingComponent.content"
              placeholder="请输入视频URL"
              style="width: 100%"
            >
              <el-button slot="append" @click="handleChooseVideo" :loading="uploading">选择视频</el-button>
            </el-input>
          </el-form-item>
        </el-form>
        <div v-if="editingComponent.content" class="video-preview">
          <video :src="editingComponent.content" controls class="preview-video"></video>
        </div>
      </template>

      <template v-if="editingComponent.type === 'text'">
        <el-form label-width="80px">
          <el-form-item label="文字内容:">
            <el-input
              type="textarea"
              v-model="editingComponent.content"
              placeholder="请输入文字内容"
              maxlength="500"
              show-word-limit
              rows="4"
              style="width: 100%"
            />
          </el-form-item>
        </el-form>
      </template>

      <template v-if="editingComponent.type === 'tag'">
        <el-form label-width="80px">
          <el-form-item label="标签内容:">
            <el-input
              v-model="editingComponent.content"
              placeholder="请输入标签内容"
              maxlength="20"
              show-word-limit
              style="width: 100%"
            />
          </el-form-item>
        </el-form>
      </template>

      <span slot="footer">
        <el-button @click="closeModal">取消</el-button>
        <el-button type="primary" @click="saveEdit">确认</el-button>
      </span>
    </el-dialog>

    <!-- 多选弹窗 -->
    <el-dialog
      :title="'添加多个' + getTypeText(selectingType)"
      :visible.sync="showMultiSelectModal"
      width="600px"
      append-to-body
      @close="closeMultiSelectModal"
    >
      <div class="multi-select-tips">
        最多可选择9个{{ getTypeText(selectingType) }}
      </div>
      <div class="selected-preview">
        <div
          v-for="(item, idx) in selectedItems"
          :key="idx"
          class="preview-item"
        >
          <div v-if="selectingType === 'smallImage'" class="image-preview-item">
            <img :src="item.previewUrl || item.url" class="preview-thumb" />
            <span class="remove-preview" @click="removeSelectedItem(idx)">×</span>
          </div>
          <div v-else-if="selectingType === 'video'" class="video-preview-item">
            <video :src="item.previewUrl || item.url" class="preview-thumb"></video>
            <span class="remove-preview" @click="removeSelectedItem(idx)">×</span>
          </div>
          <div v-else-if="selectingType === 'tag'" class="tag-preview-item">
            <div class="tag-content">
              <span class="tag-text">{{ item }}</span>
            </div>
            <span class="remove-preview" @click="removeSelectedItem(idx)">×</span>
          </div>
        </div>
      </div>
      <div class="multi-select-actions">
        <el-button @click="handleMultiSelect" :loading="uploading">
          {{ selectedItems.length > 0 ? '继续选择' : '选择' }}{{ getTypeText(selectingType) }}
        </el-button>
        <el-button
          type="primary"
          :disabled="selectedItems.length === 0"
          @click="confirmMultiSelection"
        >
          确认添加 ({{ selectedItems.length }}个)
        </el-button>
      </div>
    </el-dialog>

    <!-- 文字直接输入弹窗 -->
    <el-dialog
      title="编辑文字"
      :visible.sync="showTextInputModal"
      width="560px"
      append-to-body
      @close="closeTextInputModal"
    >
      <el-input
        type="textarea"
        v-model="directTextInput"
        placeholder="请输入文字内容"
        maxlength="500"
        show-word-limit
        rows="5"
        style="width: 100%"
      />
      <span slot="footer">
        <el-button @click="closeTextInputModal">取消</el-button>
        <el-button type="primary" @click="saveDirectTextInput">确认</el-button>
      </span>
    </el-dialog>

    <!-- 隐藏的文件选择 input -->
    <input
      type="file"
      ref="fileInput"
      style="display: none"
      :accept="fileAccept"
      :multiple="fileMultiple"
      @change="handleFileChange"
    />
  </div>
</template>

<script>
export default {
  name: 'PageEditor',
  props: {
    title: {
      type: String,
      default: '团购内容'
    },
    initialComponents: {
      type: Array,
      default: () => []
    },
    orderno: {
      type: String,
      default: ''
    },
    showFooter: {
      type: Boolean,
      default: true
    },
    autoSave: {
      type: Boolean,
      default: true
    },
    storageKey: {
      type: String,
      default: 'page_main'
    }
  },
  data () {
    return {
      componentList: [],
      activeIndex: -1,
      activeTool: '',
      showEditModal: false,
      editingIndex: -1,
      editingComponent: { type: '', content: '' },
      showMultiSelectModal: false,
      selectingType: '',
      selectedItems: [],
      showTextInputModal: false,
      directTextInput: '',
      directEditingIndex: -1,
      fileAccept: '*',
      fileMultiple: false,
      fileResolve: null,
      updatingFromParent: false,
      uploading: false // 上传中状态
    }
  },
  watch: {
    initialComponents: {
      handler (newVal) {
        const newStr = JSON.stringify(newVal || [])
        const oldStr = JSON.stringify(this.componentList)
        if (newStr !== oldStr) {
          this.updatingFromParent = true
          this.componentList = Array.isArray(newVal) ? [...newVal] : []
          this.$nextTick(() => {
            this.updatingFromParent = false
          })
        }
      },
      immediate: true
    },
    componentList: {
      handler (newVal) {
        if (!this.updatingFromParent) {
          if (this.autoSave) {
            this.saveToStorage()
          }
          this.$emit('components-update', newVal)
        }
      },
      deep: true
    }
  },
  mounted () {
    // 可选加载缓存逻辑
  },
  methods: {
    // ===== 文件上传到服务器 =====
    async uploadFileToServer (file) {
      const shopId = localStorage.getItem('sh_id') || '2'
      const token = localStorage.getItem('token') || ''

      const formData = new FormData()
      formData.append('file', file)
      formData.append('sh_id', shopId)
      formData.append('comlistToken', token)
      formData.append('sourcePath', file.name || '')

      try {
        const response = await fetch('https://caixiao.lianglianyn.com/index/upload_goods_m1.php', {
          method: 'POST',
          headers: {
            comlistToken: token
          },
          body: formData
        })
        const data = await response.json()
        if (data.code === 0) {
          // 假设返回 { url: '...', id: '...' }
          return { url: data.url, id: data.id }
        } else {
          throw new Error(data.msg || '上传失败')
        }
      } catch (error) {
        console.error('文件上传失败:', error)
        throw error
      }
    },

    // ===== 存储相关 =====
    loadFromStorage () {
      try {
        const stored = localStorage.getItem(this.storageKey)
        if (stored) {
          const data = JSON.parse(stored)
          if (data.orderno === this.orderno) {
            this.componentList = data.components || []
            this.$emit('load-success', this.componentList)
          } else {
            this.componentList = []
            this.$emit('load-empty')
          }
        } else {
          this.componentList = []
          this.$emit('load-empty')
        }
      } catch (e) {
        console.error('加载缓存失败:', e)
        this.componentList = []
        this.$emit('load-error', e)
      }
    },
    saveToStorage () {
      if (!this.autoSave) return
      try {
        const data = {
          orderno: this.orderno,
          components: this.componentList.map(item => ({
            type: item.type,
            content: item.content,
            id: item.id
          }))
        }
        localStorage.setItem(this.storageKey, JSON.stringify(data))
        this.$emit('save-success', data)
      } catch (e) {
        console.error('保存缓存失败:', e)
        this.$emit('save-error', e)
      }
    },

    // ===== 添加组件 =====
    handleAddComponent (type) {
      this.activeTool = type
      const newComponent = {
        type,
        content: this.getDefaultContent(type),
        id: Date.now() + Math.random()
      }
      this.componentList.push(newComponent)
      this.activeIndex = this.componentList.length - 1
      this.$emit('component-add', { component: newComponent, index: this.activeIndex })
      if (['bigImage', 'smallImage', 'video'].includes(type)) {
        this.handleEditComponent(this.activeIndex)
      }
    },

    handleAddTextComponent () {
      this.activeTool = 'text'
      const newComponent = {
        type: 'text',
        content: '',
        id: Date.now() + Math.random()
      }
      this.componentList.push(newComponent)
      this.activeIndex = this.componentList.length - 1
      this.$emit('component-add', { component: newComponent, index: this.activeIndex })
      this.$nextTick(() => {
        this.handleEditTextDirectly(this.activeIndex)
      })
    },

    handleAddMultipleComponents (type) {
      this.activeTool = type
      this.selectingType = type
      this.selectedItems = []
      this.showMultiSelectModal = true
    },

    // ===== 多选逻辑 =====
    async handleMultiSelect () {
      const maxCount = 9
      const remain = maxCount - this.selectedItems.length
      if (remain <= 0) {
        this.$message.warning('最多选择9个')
        return
      }

      if (this.selectingType === 'smallImage') {
        this.fileAccept = 'image/*'
        this.fileMultiple = true
        this.openFilePicker().then(async (files) => {
          if (!files || files.length === 0) return
          this.uploading = true
          const uploadPromises = files.slice(0, remain).map(async (file) => {
            try {
              const result = await this.uploadFileToServer(file)
              // 存储服务器 URL，同时保留预览 URL（临时）
              return {
                url: result.url,
                previewUrl: URL.createObjectURL(file),
                id: result.id
              }
            } catch (error) {
              this.$message.error(`文件 ${file.name} 上传失败: ${error.message}`)
              return null
            }
          })
          const results = await Promise.all(uploadPromises)
          this.uploading = false
          const valid = results.filter(r => r !== null)
          this.selectedItems.push(...valid)
          this.$emit('image-choose', valid.map(r => r.url))
        }).catch(() => {
          this.uploading = false
        })
      } else if (this.selectingType === 'video') {
        this.fileAccept = 'video/*'
        this.fileMultiple = false
        this.openFilePicker().then(async (files) => {
          if (!files || files.length === 0) return
          const file = files[0]
          this.uploading = true
          try {
            const result = await this.uploadFileToServer(file)
            this.selectedItems.push({
              url: result.url,
              previewUrl: URL.createObjectURL(file),
              id: result.id
            })
            this.$emit('video-choose', result.url)
          } catch (error) {
            this.$message.error(`视频上传失败: ${error.message}`)
          } finally {
            this.uploading = false
          }
        }).catch(() => {
          this.uploading = false
        })
      } else if (this.selectingType === 'tag') {
        this.$prompt('请输入标签，多个用逗号分隔', '添加多个标签', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          inputPattern: /\S+/,
          inputErrorMessage: '标签不能为空'
        }).then(({ value }) => {
          const tags = value.split(',').map(s => s.trim()).filter(s => s)
          if (tags.length) {
            this.selectedItems.push(...tags)
          }
        }).catch(() => {})
      }
    },

    removeSelectedItem (index) {
      this.selectedItems.splice(index, 1)
    },

    confirmMultiSelection () {
      if (this.selectedItems.length === 0) return
      const newComponents = this.selectedItems.map(item => {
        // 如果是图片或视频，item 是对象 {url, previewUrl, id}
        if (typeof item === 'object' && item.url) {
          return {
            type: this.selectingType,
            content: item.url,
            id: item.id || Date.now() + Math.random()
          }
        } else {
          // 标签直接是字符串
          return {
            type: this.selectingType,
            content: item,
            id: Date.now() + Math.random()
          }
        }
      })
      this.componentList.push(...newComponents)
      this.activeIndex = this.componentList.length - 1
      this.closeMultiSelectModal()
      newComponents.forEach((comp, idx) => {
        this.$emit('component-add', {
          component: comp,
          index: this.componentList.length - newComponents.length + idx
        })
      })
    },

    closeMultiSelectModal () {
      this.showMultiSelectModal = false
      this.selectedItems = []
      this.selectingType = ''
    },

    // ===== 文件选择辅助 =====
    openFilePicker () {
      return new Promise((resolve, reject) => {
        this.fileResolve = resolve
        this.$refs.fileInput.value = ''
        this.$refs.fileInput.click()
        setTimeout(() => reject(new Error('取消选择')), 60000)
      })
    },
    handleFileChange (e) {
      const files = e.target.files
      if (files && files.length) {
        this.fileResolve && this.fileResolve(Array.from(files))
      } else {
        this.fileResolve && this.fileResolve([])
      }
      this.fileResolve = null
      this.$refs.fileInput.value = ''
    },

    // ===== 组件操作 =====
    handleSetActive (index) {
      this.activeIndex = index
      this.$emit('component-active', { component: this.componentList[index], index })
    },

    handleEditComponent (index) {
      this.editingIndex = index
      this.editingComponent = { ...this.componentList[index] }
      this.showEditModal = true
      this.$emit('component-edit-start', {
        component: this.editingComponent,
        index
      })
    },

    handleEditTextDirectly (index) {
      this.directEditingIndex = index
      this.directTextInput = this.componentList[index].content || ''
      this.showTextInputModal = true
      this.$emit('component-edit-start', {
        component: this.componentList[index],
        index
      })
    },

    saveDirectTextInput () {
      if (this.directEditingIndex !== -1) {
        const oldComp = this.componentList[this.directEditingIndex]
        this.componentList[this.directEditingIndex].content = this.directTextInput
        this.$emit('component-edit-save', {
          oldComponent: oldComp,
          newComponent: this.componentList[this.directEditingIndex],
          index: this.directEditingIndex
        })
      }
      this.closeTextInputModal()
    },

    closeTextInputModal () {
      this.showTextInputModal = false
      this.directTextInput = ''
      this.directEditingIndex = -1
      this.$emit('component-edit-cancel')
    },

    handleDeleteComponent (index) {
      this.$confirm('确定要删除这个组件吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(() => {
        const deleted = this.componentList[index]
        this.componentList.splice(index, 1)
        if (this.activeIndex === index) this.activeIndex = -1
        this.$emit('component-delete', { component: deleted, index })
      }).catch(() => {})
    },

    handleMoveUp (index) {
      if (index > 0) {
        const temp = this.componentList[index]
        this.componentList.splice(index, 1)
        this.componentList.splice(index - 1, 0, temp)
        this.activeIndex = index - 1
        this.$emit('component-move', {
          component: temp,
          fromIndex: index,
          toIndex: index - 1
        })
      }
    },

    handleMoveDown (index) {
      if (index < this.componentList.length - 1) {
        const temp = this.componentList[index]
        this.componentList.splice(index, 1)
        this.componentList.splice(index + 1, 0, temp)
        this.activeIndex = index + 1
        this.$emit('component-move', {
          component: temp,
          fromIndex: index,
          toIndex: index + 1
        })
      }
    },

    // ===== 编辑弹窗 =====
    closeModal () {
      this.showEditModal = false
      this.editingComponent = {}
      this.editingIndex = -1
      this.$emit('component-edit-cancel')
    },

    saveEdit () {
      if (this.editingIndex !== -1) {
        const oldComp = this.componentList[this.editingIndex]
        this.componentList[this.editingIndex] = this.editingComponent
        this.$emit('component-edit-save', {
          oldComponent: oldComp,
          newComponent: this.editingComponent,
          index: this.editingIndex
        })
      }
      this.closeModal()
    },

    // ===== 编辑弹窗内选择图片/视频（调用上传） =====
    async handleChooseImage () {
      this.fileAccept = 'image/*'
      this.fileMultiple = false
      try {
        const files = await this.openFilePicker()
        if (!files || files.length === 0) return
        const file = files[0]
        this.uploading = true
        const result = await this.uploadFileToServer(file)
        this.editingComponent.content = result.url
        this.$emit('image-choose', result.url)
        this.$message.success('图片上传成功')
      } catch (error) {
        this.$message.error('图片上传失败: ' + error.message)
      } finally {
        this.uploading = false
      }
    },

    async handleChooseVideo () {
      this.fileAccept = 'video/*'
      this.fileMultiple = false
      try {
        const files = await this.openFilePicker()
        if (!files || files.length === 0) return
        const file = files[0]
        this.uploading = true
        const result = await this.uploadFileToServer(file)
        this.editingComponent.content = result.url
        this.$emit('video-choose', result.url)
        this.$message.success('视频上传成功')
      } catch (error) {
        this.$message.error('视频上传失败: ' + error.message)
      } finally {
        this.uploading = false
      }
    },

    // ===== 辅助方法 =====
    shouldUseHorizontalLayout (type) {
      return ['smallImage', 'tag'].includes(type)
    },

    getDefaultContent (type) {
      return ''
    },

    getTypeText (type) {
      const map = {
        bigImage: '大图',
        smallImage: '小图',
        video: '视频',
        text: '文字',
        tag: '标签'
      }
      return map[type] || '组件'
    },

    handlePreview () {
      if (this.componentList.length === 0) {
        this.$message.warning('请先添加内容')
        return
      }
      this.$emit('preview', this.componentList)
    },

    handlePublish () {
      if (this.componentList.length === 0) {
        this.$message.warning('请先添加内容')
        return
      }
      this.$emit('publish', this.componentList)
    },

    // ===== 公共方法 =====
    getComponents () {
      return this.componentList
    },

    setComponents (components) {
      this.componentList = [...components]
    },

    addComponent (component) {
      this.componentList.push({
        ...component,
        id: component.id || Date.now() + Math.random()
      })
    },

    clearComponents () {
      this.componentList = []
      this.activeIndex = -1
    },

    importData (data) {
      if (data && data.components) {
        this.componentList = data.components.map(comp => ({
          ...comp,
          id: comp.id || Date.now() + Math.random()
        }))
      }
    },

    exportData () {
      return {
        orderno: this.orderno,
        components: this.componentList
      }
    }
  }
}
</script>

<style scoped>
/* ===== 全局布局 ===== */
.page-editor {
  display: flex;
  flex-direction: column;
  height: 100vh;
  overflow: hidden;
  background: #f0f2f5;
  font-family: -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Helvetica, 'PingFang SC', 'Microsoft YaHei', sans-serif;
  max-width: 1200px;
  margin: 0 auto;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.08);
}

/* ===== 工具栏 ===== */
.toolbar {
  background: #ffffff;
  border-bottom: 1px solid #e8ecf1;
  padding: 16px 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  flex-shrink: 0;
}

.tool-title {
  font-size: 20px;
  font-weight: 600;
  color: #1a1a1a;
  margin-bottom: 12px;
  text-align: center;
}

.tool-items {
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
}

.tool-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 10px 18px;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  min-width: 80px;
  background: #f7f8fa;
  border: 2px solid transparent;
}

.tool-item:hover {
  background: #e8ecf1;
  transform: translateY(-2px);
}

.tool-item.active {
  background: #e6f0ff;
  border-color: #409eff;
}

.tool-icon {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  margin-bottom: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  background: #d9e1ec;
}

.big-image-icon {
  background: linear-gradient(135deg, #ff9a9e, #fad0c4);
}
.big-image-icon::after {
  content: '';
  width: 24px;
  height: 24px;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.1);
}

.small-image-icon {
  background: linear-gradient(135deg, #a1c4fd, #c2e9fb);
}
.small-image-icon::after {
  content: '';
  width: 18px;
  height: 18px;
  background: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 6px rgba(0,0,0,0.1);
}

.video-icon {
  background: linear-gradient(135deg, #ffecd2, #fcb69f);
}
.video-icon::after {
  content: '';
  border-left: 12px solid #fff;
  border-top: 8px solid transparent;
  border-bottom: 8px solid transparent;
  margin-left: 4px;
}

.text-icon {
  background: linear-gradient(135deg, #84fab0, #8fd3f4);
}
.text-icon::after {
  content: 'T';
  color: #fff;
  font-size: 20px;
  font-weight: bold;
}

.tag-icon {
  background: linear-gradient(135deg, #d4fc79, #96e6a1);
}
.tag-icon::after {
  content: '#';
  color: #fff;
  font-size: 20px;
  font-weight: bold;
}

.tool-text {
  font-size: 13px;
  color: #4a4a4a;
  font-weight: 500;
}
.tool-item.active .tool-text {
  color: #409eff;
}

/* ===== 内容区域 ===== */
.content-area {
  flex: 1;
  padding: 20px 24px;
  overflow-y: auto;
  background: #f7f9fc;
}

.component-item {
  position: relative;
  margin-bottom: 20px;
  background: #ffffff;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  border: 2px solid transparent;
  transition: all 0.25s ease;
}

.component-item.active {
  border-color: #409eff;
  box-shadow: 0 4px 20px rgba(64, 158, 255, 0.15);
  transform: translateY(-2px);
}

.component-item.horizontal-layout {
  padding: 16px 20px;
}

.component-type-tag {
  position: absolute;
  top: 12px;
  left: 16px;
  background: rgba(0, 0, 0, 0.65);
  color: #fff;
  padding: 2px 14px;
  border-radius: 20px;
  font-size: 12px;
  z-index: 2;
  letter-spacing: 0.5px;
}

/* ===== 大图 ===== */
.big-image {
  width: 100%;
  display: block;
  max-height: 400px;
  object-fit: cover;
  cursor: pointer;
}

/* ===== 小图 ===== */
.small-image-component {
  display: flex;
  align-items: center;
  padding: 12px 16px;
}
.small-image {
  width: 160px;
  height: 120px;
  object-fit: cover;
  border-radius: 10px;
  margin-right: 20px;
  cursor: pointer;
}

/* ===== 视频 ===== */
.video-player {
  width: 100%;
  max-height: 360px;
  background: #000;
  cursor: pointer;
}

/* ===== 文字 ===== */
.text-component {
  padding: 20px 24px;
}
.text-content {
  font-size: 16px;
  line-height: 1.7;
  color: #333;
  cursor: pointer;
}
.text-content:hover {
  color: #409eff;
}

/* ===== 标签 ===== */
.tag-component {
  padding: 16px 20px;
}
.tag-content {
  display: inline-block;
  background: linear-gradient(135deg, #f093fb, #f5576c);
  padding: 8px 24px;
  border-radius: 40px;
  box-shadow: 0 4px 12px rgba(245, 87, 108, 0.3);
  cursor: pointer;
}
.tag-text {
  font-size: 14px;
  color: #fff;
  font-weight: 500;
}

/* ===== 组件操作按钮 ===== */
.component-actions {
  padding: 12px 20px;
  border-top: 1px solid #f0f2f5;
  background: #fafbfc;
  display: flex;
  justify-content: flex-end;
}
.action-btn-group {
  display: flex;
  gap: 10px;
}

/* ===== 空状态 ===== */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 0;
  text-align: center;
}
.empty-icon {
  width: 120px;
  height: 120px;
  background: linear-gradient(135deg, #a1c4fd, #c2e9fb);
  border-radius: 50%;
  margin-bottom: 24px;
  position: relative;
}
.empty-icon::after {
  content: '+';
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 48px;
  color: #fff;
  font-weight: 200;
}
.empty-title {
  font-size: 20px;
  color: #1a1a1a;
  margin-bottom: 8px;
  font-weight: 600;
}
.empty-desc {
  font-size: 14px;
  color: #999;
}

/* ===== 底部栏 ===== */
.footer {
  background: #ffffff;
  border-top: 1px solid #e8ecf1;
  padding: 12px 24px;
  flex-shrink: 0;
}
.footer-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.component-count {
  font-size: 14px;
  color: #666;
}
.footer-actions {
  display: flex;
  gap: 12px;
}

/* ===== 弹窗内的预览 ===== */
.image-preview,
.video-preview {
  margin-top: 12px;
}
.preview-image {
  width: 100%;
  max-height: 300px;
  object-fit: contain;
  border-radius: 8px;
  border: 1px solid #e8ecf1;
}
.preview-video {
  width: 100%;
  max-height: 300px;
  border-radius: 8px;
  background: #000;
}

/* ===== 多选弹窗预览 ===== */
.multi-select-tips {
  text-align: center;
  color: #909399;
  font-size: 14px;
  margin-bottom: 16px;
}
.selected-preview {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  max-height: 300px;
  overflow-y: auto;
  padding: 12px;
  background: #f7f9fc;
  border-radius: 8px;
  min-height: 60px;
  border: 1px dashed #d9d9d9;
  margin-bottom: 16px;
}
.preview-item {
  position: relative;
}
.image-preview-item,
.video-preview-item {
  width: 120px;
  height: 120px;
  border-radius: 10px;
  overflow: hidden;
  border: 1px solid #e8ecf1;
}
.preview-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.tag-preview-item .tag-content {
  background: linear-gradient(135deg, #f093fb, #f5576c);
  padding: 6px 20px;
  border-radius: 40px;
  font-size: 14px;
  color: #fff;
}
.remove-preview {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 24px;
  height: 24px;
  background: #f56c6c;
  color: #fff;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  cursor: pointer;
  box-shadow: 0 2px 6px rgba(0,0,0,0.15);
}
.remove-preview:hover {
  background: #e74c3c;
}
.multi-select-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}
</style>
